import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Alert, AlertDescription } from '../ui/alert'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { AlertCircle, Loader2, XCircle, Calendar, Pause } from 'lucide-react'
import { SubscriptionTypeEnum } from '../../lib/enums'

interface CancellationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentTier: SubscriptionTypeEnum
  renewsAt?: string
  onConfirmCancel: (reason: string) => Promise<void>
  onPauseInstead?: () => void
  loading?: boolean
}

const cancellationReasons = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'not_using', label: 'Not using enough' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'switching_service', label: 'Switching to another service' },
  { value: 'temporary', label: 'Taking a temporary break' },
  { value: 'other', label: 'Other reason' },
]

type CancellationStep = 'reason' | 'retention' | 'confirm'

const CancellationDialog: FC<CancellationDialogProps> = ({
  open,
  onOpenChange,
  currentTier,
  renewsAt,
  onConfirmCancel,
  onPauseInstead,
  loading = false,
}) => {
  const [step, setStep] = useState<CancellationStep>('reason')
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [confirming, setConfirming] = useState(false)

  const isPremiumOrHigher = currentTier !== SubscriptionTypeEnum.Basic

  const handleConfirm = async () => {
    if (!selectedReason) return

    setConfirming(true)
    try {
      await onConfirmCancel(selectedReason)
      onOpenChange(false)
      // Reset state for next time
      setTimeout(() => {
        setStep('reason')
        setSelectedReason('')
      }, 300)
    } finally {
      setConfirming(false)
    }
  }

  const handleNext = () => {
    if (step === 'reason' && selectedReason) {
      // Show retention step for temporary cancellations
      if (selectedReason === 'temporary' && onPauseInstead) {
        setStep('retention')
      } else {
        setStep('confirm')
      }
    } else if (step === 'retention') {
      setStep('confirm')
    }
  }

  const handleBack = () => {
    if (step === 'confirm') {
      setStep(selectedReason === 'temporary' && onPauseInstead ? 'retention' : 'reason')
    } else if (step === 'retention') {
      setStep('reason')
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    // Reset state
    setTimeout(() => {
      setStep('reason')
      setSelectedReason('')
    }, 300)
  }

  const featuresLost = currentTier === SubscriptionTypeEnum.Premium
    ? [
        'Barcode generation & printing',
        'Barcode scanning',
        'Export inventory CSV',
        'Advanced reporting',
        'Enhanced analytics',
        'Priority support',
      ]
    : currentTier === SubscriptionTypeEnum.Enterprise
    ? [
        'Unlimited inventory items',
        'Multi-user access',
        'API access',
        'Custom integrations',
        'Additional stores',
        'Custom CSV reporting',
        'Dedicated support',
        'Custom training',
      ]
    : []

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-error500" />
            Cancel Subscription
          </DialogTitle>
          <DialogDescription>
            We're sorry to see you go. Help us understand why you're leaving.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Reason Selection */}
        {step === 'reason' && (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-3 block">
                What's your main reason for cancelling?
              </Label>
              <RadioGroup value={selectedReason} onValueChange={setSelectedReason}>
                <div className="space-y-3">
                  {cancellationReasons.map((reason) => (
                    <div
                      key={reason.value}
                      className="flex items-center space-x-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-800 hover:border-sumoPrimary dark:hover:border-sumoPrimary transition-colors cursor-pointer"
                      onClick={() => setSelectedReason(reason.value)}
                    >
                      <RadioGroupItem value={reason.value} id={reason.value} />
                      <Label
                        htmlFor={reason.value}
                        className="flex-1 cursor-pointer text-neutral-700 dark:text-neutral-300"
                      >
                        {reason.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium">Your feedback helps us improve</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                  We take all feedback seriously and use it to make InventorySumo better for everyone.
                </p>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Step 2: Retention Offer */}
        {step === 'retention' && (
          <div className="space-y-6">
            <Alert className="bg-sumoPrimary/5 border-sumoPrimary">
              <Pause className="h-4 w-4 text-sumoPrimary" />
              <AlertDescription>
                <p className="font-medium text-sumoPrimary">Taking a break? Try pausing instead!</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                  Pause your subscription for up to 3 months. Your data stays safe, and you won't be charged
                  during the pause period. You can resume anytime.
                </p>
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border-2 border-sumoPrimary rounded-lg bg-sumoPrimary/5">
                <div className="flex items-center gap-2 mb-2">
                  <Pause className="w-5 h-5 text-sumoPrimary" />
                  <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Pause</h4>
                </div>
                <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <li>✓ Keep all your data</li>
                  <li>✓ No charges during pause</li>
                  <li>✓ Resume anytime</li>
                  <li>✓ Up to 3 months</li>
                </ul>
              </div>

              <div className="p-4 border-2 border-neutral-200 dark:border-neutral-800 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-5 h-5 text-error500" />
                  <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">Cancel</h4>
                </div>
                <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <li>⚠ Lose premium features</li>
                  <li>⚠ Downgrade to Basic</li>
                  <li>⚠ Limited functionality</li>
                  <li>✓ Can reactivate later</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 'confirm' && (
          <div className="space-y-6">
            <Alert variant="warning" className="border-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-semibold text-warning700 dark:text-warning300">
                  Are you sure you want to cancel?
                </p>
              </AlertDescription>
            </Alert>

            {isPremiumOrHigher && (
              <div className="space-y-3">
                <h4 className="font-semibold text-neutral-900 dark:text-neutral-100">
                  You'll lose access to:
                </h4>
                <div className="space-y-2">
                  {featuresLost.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-error500" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 mt-0.5 text-neutral-600 dark:text-neutral-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    Your subscription will remain active until:
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                    {renewsAt ? new Date(renewsAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'the end of your current billing period'}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                    After that, your account will be downgraded to the Basic plan.
                    You can reactivate your subscription anytime.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="mt-6">
          {step === 'reason' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Keep Subscription
              </Button>
              <Button
                variant="destructive"
                onClick={handleNext}
                disabled={!selectedReason}
              >
                Continue
              </Button>
            </>
          )}

          {step === 'retention' && (
            <>
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  onPauseInstead?.()
                  handleClose()
                }}
              >
                <Pause className="w-4 h-4 mr-2" />
                Pause Instead
              </Button>
              <Button variant="outline" onClick={handleNext}>
                No, Cancel Anyway
              </Button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={handleBack} disabled={confirming || loading}>
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={confirming || loading}
              >
                {confirming || loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Confirm Cancellation'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CancellationDialog
